<template>
  <div class="ca-apply" v-loading="!pageData && !pageDataError">
    <!-- pageData 拉取失败或业务类型为空 → 页面级占位 + 重试（后端可能还没给该客户配置业务类型） -->
    <div v-if="pageDataError || (pageData && !productList.length)" class="ca-apply__error">
      <p style="margin-bottom: 20px">暂无业务类型信息，请联系客户人员</p>
      <el-button size="small" @click="loadPageData()">重试</el-button>
    </div>

    <div v-else-if="pageData" class="ca-apply__body">
      <aside class="ca-apply__side">
        <!-- 产品图：取 applyInfo.system.productFileId 配置，未配置或加载失败用本地 CFCA 图兜底 -->
        <img class="ca-apply__product" :src="productImgSrc" @error="handleProductImgError" />
      </aside>

      <!-- 不用 <main>：IE11 不认识该标签会按 inline 未知元素渲染，flex 布局失效 -->
      <div class="ca-apply__main">
        <!-- 页面标题 + 特殊平台红色说明（仅命中特殊平台时展示，如「中国信达集采系统(投标人)」） -->
        <div class="ca-apply__head">
          <h2 class="ca-apply__page-title">CA在线申请</h2>
          <p v-if="platformNotice" class="ca-apply__notice">
            {{ platformNotice }}
          </p>
        </div>

        <!-- 1. 选择使用平台：一个客户一个平台（customer.platformName），固定选中展示 -->
        <section class="ca-apply__section">
          <h3 class="ca-apply__section-title">1. 选择使用平台</h3>
          <div class="ca-apply__platform-group">
            <button
              v-if="pageData.customer"
              type="button"
              class="ca-apply__option ca-apply__platform-item is-active"
            >
              {{ pageData.customer.platformName }}
            </button>
          </div>
        </section>

        <!-- 2. 选择业务类型（bizKind 0-申请 1-续费） -->
        <section class="ca-apply__section">
          <h3 class="ca-apply__section-title">2. 选择业务类型</h3>
          <div class="ca-apply__biz-grid">
            <button
              v-for="b in productList"
              :key="b.id"
              type="button"
              class="ca-apply__option ca-apply__biz-item"
              :class="{ 'is-active': bizType && bizType.id === b.id }"
              @click="handleBizTypeChange(b)"
            >
              {{ b.productName }}
            </button>
          </div>
          <p v-if="renewNotice" class="ca-apply__notice">{{ renewNotice }}</p>
        </section>

        <!-- 3. 确认申请数量（上海CA 不支持多数量申请，隐藏本板块、数量固定为 1，见 showQuantity） -->
        <section v-if="showQuantity" class="ca-apply__section">
          <h3 class="ca-apply__section-title">3. 确认申请数量</h3>
          <el-input-number v-model="numberVal" :min="1" :max="100" :precision="0" @blur="handleQuantityBlur" />
          <span class="ca-apply__aux">
            可手动输入，共计 <b class="ca-apply__aux-num">{{ numberVal }}</b> 张
          </span>
        </section>

        <!-- 4. 选择申请年限（序号经 sectionNo 换算：数量板块隐藏时前移为 3） -->
        <section class="ca-apply__section">
          <h3 class="ca-apply__section-title">{{ sectionNo(4) }}. 选择申请年限</h3>
          <div class="ca-apply__year-group">
            <button
              v-for="y in limitList"
              :key="y.id"
              type="button"
              class="ca-apply__option ca-apply__year-item"
              :class="{ 'is-active': currentYear && currentYear.id === y.id }"
              @click="handleYearChange(y)"
            >
              {{ y.limitName }}
            </button>
          </div>
        </section>

        <!-- 5. 是否需要加急（仅当前年限档 urgentShow===1 时展示；提示文案取客户配置 urgentNotice） -->
        <section v-if="urgentShow" class="ca-apply__section">
          <h3 class="ca-apply__section-title">
            {{ sectionNo(5) }}. 是否需要加急
            <span v-if="urgentNotice" class="ca-apply__section-tip">（{{ urgentNotice }}）</span>
          </h3>
          <div class="ca-apply__urgent-group">
            <button
              v-for="opt in urgentOptions"
              :key="opt.value"
              type="button"
              class="ca-apply__option ca-apply__urgent-item"
              :class="{ 'is-active': isUrgent === opt.value }"
              @click="isUrgent = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </section>

        <!-- 6/7. 表单区：CFCA 与上海CA 两套字段直接写在模板里，按 caType 二选一渲染。
             维护口径：改字段（文案/占位/文件类型）只动本模板；校验规则与编辑页共享
             （util/caForm.js），上海CA 条件字段显隐按 productValue 白名单在本组件内判定；
             提交映射见 buildPayload。
             区块展示序号经 sectionNo() 换算：数量板块（上海CA 隐藏）/ 加急区隐藏时其后区块前移 -->
        <el-form ref="form" class="ca-apply__form" :model="form" :rules="rules" label-width="180px">
          <!-- ============ CFCA 表单：被授权人信息 + 申请单位信息 ============ -->
          <template v-if="caType === 'CFCA'">
            <section class="ca-apply__section">
              <h3 class="ca-apply__section-title">{{ sectionNo(6) }}. 被授权人信息</h3>
              <!-- 授权提示：取客户配置 authorizeNotice，未配置不渲染；正文按配置换行展示 -->
              <div v-if="authorizeNotice" class="ca-apply__notice-box">
                <p class="ca-apply__notice-box-title">温馨提示</p>
                <p class="ca-apply__notice-box-text">{{ authorizeNotice }}</p>
              </div>
              <div class="ca-apply__form-grid">
                <el-form-item label="被授权经办人姓名" prop="linkMan">
                  <el-input v-model.trim="form.linkMan" placeholder="请输入被授权经办人姓名" />
                </el-form-item>
                <el-form-item label="手机号" prop="linkTel">
                  <el-input v-model.trim="form.linkTel" placeholder="请输入11位手机号" />
                </el-form-item>
                <el-form-item label="身份证号" prop="identityCode">
                  <el-input
                    v-model.trim="form.identityCode"
                    placeholder="请输入18位身份证号"
                    @input="uppercase('identityCode')"
                  />
                </el-form-item>
                <el-form-item label="邮箱" prop="linkEmail">
                  <el-input
                    v-model.trim="form.linkEmail"
                    placeholder="请输入邮箱"
                    :maxlength="EMAIL_MAX_LENGTH"
                  />
                </el-form-item>
                <el-form-item label="CA邮寄地址" prop="postAddr" class="ca-apply__form-item--full">
                  <el-input
                    v-model.trim="form.postAddr"
                    type="textarea"
                    :rows="2"
                    placeholder="请输入CA邮寄地址（省市区+详细地址）"
                  />
                </el-form-item>
              </div>
            </section>
            <section class="ca-apply__section">
              <h3 class="ca-apply__section-title">{{ sectionNo(7) }}. 申请单位信息</h3>
              <div class="ca-apply__form-grid">
                <el-form-item label="单位名称" prop="applyOrgName">
                  <el-input
                    v-model.trim="form.applyOrgName"
                    placeholder="请输入单位名称"
                    :maxlength="ORG_NAME_MAX_LENGTH"
                  />
                </el-form-item>
                <el-form-item label="统一社会信用代码" prop="licenceCode">
                  <el-input
                    v-model.trim="form.licenceCode"
                    placeholder="请输入18位统一社会信用代码"
                    @input="uppercase('licenceCode')"
                  />
                </el-form-item>
                <el-form-item label="法定代表人姓名" prop="legalName">
                  <el-input v-model.trim="form.legalName" placeholder="请输入法定代表人姓名" />
                </el-form-item>
                <el-form-item label="法定代表人身份证号" prop="legalIdNo">
                  <el-input
                    v-model.trim="form.legalIdNo"
                    placeholder="请输入18位身份证号"
                    @input="uppercase('legalIdNo')"
                  />
                </el-form-item>
                <el-form-item
                  label="营业执照扫描件"
                  prop="licenceCodeFile"
                  class="ca-apply__form-item--full"
                >
                  <file-upload
                    :file-id="form.licenceCodeFile"
                    @fileIdCb="(res) => handleFileChange('licenceCodeFile', res)"
                  />
                </el-form-item>
                <el-form-item
                  label="CA申请授权书"
                  prop="authFileId"
                  class="ca-apply__form-item--full"
                >
                  <!-- 授权书为 pdf 文档（其余附件都是 jpg/png 图片）；格式提示由 filetypes 自动推导 -->
                  <file-upload
                    :file-id="form.authFileId"
                    :filetypes="['pdf']"
                    @fileIdCb="(res) => handleFileChange('authFileId', res)"
                  />
                  <!-- 模板下载链接（applyInfo.templateFileList 中 templateCode === 'SQS' 的文件） -->
                  <div v-if="authTemplateFile" class="ca-apply__template">
                    <span
                      class="ca-apply__template-link"
                      @click="download(authTemplateFile.fileId)"
                    >
                      模板下载
                    </span>
                  </div>
                </el-form-item>
              </div>
            </section>
          </template>

          <!-- ============ 上海CA 表单：经办人/申请单位信息 + 收件信息 ============ -->
          <template v-else>
            <section class="ca-apply__section">
              <h3 class="ca-apply__section-title">{{ sectionNo(6) }}. 经办人/申请单位信息</h3>
              <!-- 授权提示：取客户配置 authorizeNotice，未配置不渲染；正文按配置换行展示 -->
              <div v-if="authorizeNotice" class="ca-apply__notice-box">
                <p class="ca-apply__notice-box-title">温馨提示</p>
                <p class="ca-apply__notice-box-text">{{ authorizeNotice }}</p>
              </div>
              <div class="ca-apply__form-grid">
                <el-form-item label="经办人姓名" prop="linkMan">
                  <el-input v-model.trim="form.linkMan" placeholder="请输入经办人姓名" />
                </el-form-item>
                <el-form-item label="经办人身份证号" prop="identityCode">
                  <el-input
                    v-model.trim="form.identityCode"
                    placeholder="请输入18位身份证号"
                    @input="uppercase('identityCode')"
                  />
                </el-form-item>
                <el-form-item label="经办人手机号" prop="linkTel">
                  <el-input v-model.trim="form.linkTel" placeholder="请输入11位手机号" />
                </el-form-item>
                <!-- 经办人身份证扫描件：固定字段（非条件字段），全业务类型恒显必填 -->
                <el-form-item label="经办人身份证扫描件" prop="identityFileId">
                  <file-upload
                    :file-id="form.identityFileId"
                    @fileIdCb="(res) => handleFileChange('identityFileId', res)"
                  />
                </el-form-item>
                <el-form-item label="经办人邮箱" prop="linkEmail">
                  <el-input
                    v-model.trim="form.linkEmail"
                    placeholder="请输入邮箱"
                    :maxlength="EMAIL_MAX_LENGTH"
                  />
                </el-form-item>
                <!-- 条件字段（显示即必填）：显隐由 showLegalFields / showOrgSealFile / showLegalSealFile
                     三个 computed 按当前业务类型的 productValue 白名单控制（见各自注释）。
                     法定代表人姓名已全业务放开（恒显必填，不属于条件字段）；
                     法人证书类业务下法定代表人字段置灰、锁定为经办人值（legalFieldsDisabled） -->
                <el-form-item label="法定代表人姓名" prop="legalName">
                  <el-input
                    v-model.trim="form.legalName"
                    :placeholder="legalFieldsDisabled ? '带出经办人姓名' : '请输入法定代表人姓名'"
                    :disabled="legalFieldsDisabled"
                  />
                </el-form-item>
                <el-form-item v-if="showLegalFields" label="法定代表人身份证号" prop="legalIdNo">
                  <el-input
                    v-model.trim="form.legalIdNo"
                    :placeholder="legalFieldsDisabled ? '带出经办人身份证号' : '请输入18位身份证号'"
                    :disabled="legalFieldsDisabled"
                    @input="uppercase('legalIdNo')"
                  />
                </el-form-item>
                <el-form-item label="单位名称" prop="applyOrgName">
                  <el-input
                    v-model.trim="form.applyOrgName"
                    placeholder="请输入单位名称"
                    :maxlength="ORG_NAME_MAX_LENGTH"
                  />
                </el-form-item>
                <el-form-item label="统一社会信用代码" prop="licenceCode">
                  <el-input
                    v-model.trim="form.licenceCode"
                    placeholder="请输入18位统一社会信用代码"
                    @input="uppercase('licenceCode')"
                  />
                </el-form-item>
                <!-- 营业执照扫描件：仅「申请单位证书 / 续费单位证书」业务类型展示（显示即必填） -->
                <el-form-item
                  v-if="showLicenceCodeFile"
                  label="营业执照扫描件"
                  prop="licenceCodeFile"
                >
                  <file-upload
                    :file-id="form.licenceCodeFile"
                    @fileIdCb="(res) => handleFileChange('licenceCodeFile', res)"
                  />
                </el-form-item>
                <el-form-item
                  v-if="showLegalFields"
                  label="法定代表人身份证扫描件"
                  prop="legalIdFileId"
                >
                  <file-upload
                    :file-id="form.legalIdFileId"
                    @fileIdCb="(res) => handleFileChange('legalIdFileId', res)"
                  />
                </el-form-item>
                <el-form-item v-if="showOrgSealFile" label="单位公章图片" prop="orgSealFileId">
                  <file-upload
                    :file-id="form.orgSealFileId"
                    :max-size="5"
                    @fileIdCb="(res) => handleFileChange('orgSealFileId', res)"
                  />
                  <!-- 模板下载链接（applyInfo.templateFileList 中 templateCode === 'DWGZ' 的文件） -->
                  <div v-if="orgSealTemplateFile" class="ca-apply__template">
                    <span
                      class="ca-apply__template-link"
                      @click="download(orgSealTemplateFile.fileId)"
                    >
                      模板下载
                    </span>
                  </div>
                </el-form-item>
                <el-form-item
                  v-if="showLegalSealFile"
                  label="法人私章/手写签字图片"
                  prop="legalSealFileId"
                >
                  <file-upload
                    :file-id="form.legalSealFileId"
                    :max-size="5"
                    @fileIdCb="(res) => handleFileChange('legalSealFileId', res)"
                  />
                  <!-- 模板下载链接（applyInfo.templateFileList 中 templateCode === 'DWGZ' 的文件） -->
                  <div v-if="orgSealTemplateFile" class="ca-apply__template">
                    <span
                      class="ca-apply__template-link"
                      @click="download(orgSealTemplateFile.fileId)"
                    >
                      模板下载
                    </span>
                  </div>
                </el-form-item>
              </div>
            </section>
            <section class="ca-apply__section">
              <h3 class="ca-apply__section-title">
                {{ sectionNo(7) }}. 收件信息
                <span class="ca-apply__section-tip">（CA证书邮寄收件信息）</span>
              </h3>
              <div class="ca-apply__form-grid">
                <el-form-item label="收件人姓名" prop="receiverName">
                  <el-input v-model.trim="form.receiverName" placeholder="请输入收件人姓名" />
                </el-form-item>
                <el-form-item label="收件人手机号" prop="receiverMobile">
                  <el-input v-model.trim="form.receiverMobile" placeholder="请输入11位手机号" />
                </el-form-item>
                <el-form-item label="收件地址" prop="postAddr" class="ca-apply__form-item--full">
                  <div class="ca-apply__addr">
                    <el-form-item prop="receiverArea" class="ca-apply__addr-area">
                      <el-cascader
                        ref="receiverAreaCascader"
                        v-model="form.receiverArea"
                        :options="areaOptions"
                        placeholder="请选择省/市/区县"
                        @change="handleReceiverAreaChange"
                      />
                    </el-form-item>
                    <el-input
                      v-model.trim="form.postAddr"
                      class="ca-apply__addr-detail"
                      type="textarea"
                      autosize
                      placeholder="请输入详细收件地址（街道/门牌号等，含省市区）"
                    />
                  </div>
                </el-form-item>
              </div>
            </section>
          </template>
        </el-form>

        <div class="ca-apply__form-notice">
          <p v-if="caType === 'CFCA' && deliveryNotice">{{ deliveryNotice }}</p>
          <p v-if="refundNotice" class="tip">{{ refundNotice }}</p>
        </div>

        <div class="ca-apply__settle">
          <div class="ca-apply__settle-row ca-apply__total">
            总计: <b>¥ {{ total }}</b>
          </div>
          <div class="ca-apply__settle-row ca-apply__paytype">
            支付方式：
            <el-radio-group v-model="payType">
              <el-radio :label="PAY_TYPE.ONLINE">网上支付</el-radio>
              <el-radio :label="PAY_TYPE.WIRE">电汇</el-radio>
            </el-radio-group>
          </div>
          <div class="ca-apply__settle-row">
            <el-button
              type="danger"
              class="ca-apply__settle-btn"
              :loading="submitting"
              @click="handleSettle"
            >
              去结算
            </el-button>
          </div>
        </div>

        <div
          v-if="pageData.templateFileList && pageData.templateFileList.length"
          class="ca-apply__files"
        >
          <div class="ca-apply__files-label">相关文件下载：</div>
          <div class="ca-apply__files-list">
            <span
              v-for="f in pageData.templateFileList"
              :key="f.fileId"
              class="ca-apply__file-chip"
              @click="download(f.fileId)"
            >
              {{ f.fileName }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <confirm-dialog
      :visible.sync="confirmVisible"
      :snapshot="confirmSnapshot"
      :confirming="submitting"
      @confirm="handleConfirmSubmit"
    />
    <pay-result-dialog
      :visible.sync="payResultVisible"
      @problem="handlePayProblem"
      @done="handlePayDone"
    />
  </div>
</template>

<script>
import FileUpload from '../components/FileUpload.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import PayResultDialog from '../components/PayResultDialog.vue'
import {
  PAY_TYPE,
  CA_TYPE,
  EMAIL_MAX_LENGTH,
  ORG_NAME_MAX_LENGTH,
  TEMPLATE_CODE,
  CA_FORM_KEYS,
  buildCaFormRules,
} from '../util/caForm'
import { normalizeQuantity, calcTotalPrice } from '../util/calc'
import { payCaOrder } from '../util/pay'
import { getPageData, submitOrder, paySign } from '@/api/ca'
import { isLogin, showLogin } from '@/shared/user'
import { download } from '@/shared/file'
import { data as cityData } from '@/util/cityData'

export default {
  name: 'CaApply',
  components: { FileUpload, ConfirmDialog, PayResultDialog },
  data() {
    return {
      PAY_TYPE,
      EMAIL_MAX_LENGTH,
      ORG_NAME_MAX_LENGTH,
      pageData: null, // 当前客户的页面配置（平台 / 业务类型 / 年限档 / 下载文件），/zcjf/ca/apply/applyInfo 拉取
      pageDataError: false, // applyInfo 拉取失败或为空 → 页面级错误占位 + 重试按钮
      platformName: '', // 使用平台名（取接口 customer.platformName，与页面「1. 选择使用平台」同源；用于确认弹框快照）
      bizType: null, // 选中的业务类型对象（applyInfo.productList 中的一项，含 id / limitList 等）
      numberVal: 1, // 申请数量（正整数，非法输入失焦兜底为 1）
      currentYear: null, // 选中的申请年限档（含 id / months / price / urgentFlag / urgentFee）
      isUrgent: 0, // 是否加急：0 不需要（默认）/ 1 需要；仅当前年限档 urgentShow=1 时可选
      payType: PAY_TYPE.ONLINE, // 支付方式：默认网上支付；随「去结算」带入确认弹框
      form: {}, // 表单字段值（key 见 util/caForm.js 的 CA_FORM_KEYS，created 里预注册全部 key 保证响应式）
      areaOptions: cityData, // 收件地区级联数据源（省/市/区县三级区划码）
      confirmVisible: false, // 申请信息确认弹框开关
      payResultVisible: false, // 支付结果确认弹框开关（收银台打开后弹出，与订单列表页同款）
      paidOrderId: '', // 已发起支付的订单号（支付结果弹框「已完成支付」跳详情页用）
      submitting: false, // 下单 / 支付请求中 → 按钮 loading 防重复提交
      pendingSettle: false, // 「去结算」因未登录被拦下：登录成功后补开确认弹框
    }
  },
  computed: {
    // 当前客户编码（路由守卫写入 store）：页面据此拉取 applyInfo，并监听其变化重新拉取
    customerCode() {
      return this.$store.state.ca.customerCode
    },
    // 使用平台信息（applyInfo.system：sysName / productFileId 等），未就绪为 null
    system() {
      return (this.pageData && this.pageData.system) || null
    },
    // 业务类型列表（applyInfo.productList，后端配置下发；为空时页面显示占位 + 重试）
    productList() {
      return (this.pageData && this.pageData.productList) || []
    },
    // 登录弹框可见性：CaLoginDialog（CaLayout 中挂载）绑定 store 的 user/loginVisible，
    // 登录成功后 login action 会置 false 并写入 token，这里据此补开结算确认弹框
    loginVisible() {
      return this.$store.state.user.loginVisible
    },
    // 当前 CA 类型枚举（'CFCA' / 'SHCA'）：applyInfo.customer.caVendor 仅 CFCA / SHECA（上海CA）两种，
    // 优先按 CFCA 判断；决定渲染哪套表单
    caType() {
      const customer = (this.pageData && this.pageData.customer) || {}
      return customer.caVendor === 'CFCA' ? CA_TYPE.CFCA : CA_TYPE.SHCA
    },
    // 上海CA 不支持多数量申请：隐藏「确认申请数量」板块，数量固定为 1（见 quantity）
    showQuantity() {
      return this.caType === CA_TYPE.CFCA
    },
    // 实际生效的申请数量：数量板块隐藏（上海CA）时恒为 1，否则取用户输入
    quantity() {
      return this.showQuantity ? this.numberVal : 1
    },
    // 表单校验规则：按 caType 生成（与编辑页共享，见 util/caForm.js）；
    // 上海CA 条件字段靠模板上 v-if 的 el-form-item 自动注销、不参与校验，无需按业务类型裁剪
    rules() {
      // 仅上海CA 传 receiverArea getter：级联与详细地址都为空时只提示「请选择收件地区」，不重复报详细地址。
      // CFCA 无收件地区级联（receiverArea 恒为空数组），若也传 getter 会让 postAddr 被误判为
      // 「已由级联兜底」而整条跳过校验，必须传 undefined 走独立必填校验
      const getReceiverArea = this.caType === CA_TYPE.SHCA ? () => this.form.receiverArea : undefined
      return buildCaFormRules(this.caType, getReceiverArea)
    },
    // 选中业务类型的 value（productList 下发的 productValue，后端固定的 1-8，
    // 含义见 util/caForm.js 的 BIZ_TYPE_OPTIONS）：上海CA 条件字段显隐的判定依据
    productValue() {
      return this.bizType ? Number(this.bizType.productValue) : null
    },
    // 上海CA 条件字段显隐（显示即必填），按 productValue 白名单判定：
    // 法定代表人身份证号 + 身份证扫描件（法定代表人姓名已全业务放开，恒显必填不在此列）：
    // value ∈ {1 申请单位证书+法人证书, 2 续费单位证书+法人证书, 5 申请法人证书, 6 续费法人证书}
    showLegalFields() {
      return [1, 2, 5, 6].includes(this.productValue)
    },
    // 营业执照扫描件：value ∈ {3 申请单位证书, 4 续费单位证书}
    showLicenceCodeFile() {
      return [3, 4].includes(this.productValue)
    },
    // 单位公章图片：value ∈ {1 申请单位证书+法人证书, 3 申请单位证书, 7 申请单位证书(境外)}
    showOrgSealFile() {
      return [1, 3, 7].includes(this.productValue)
    },
    // 法人私章/手写签字图片：value ∈ {1 申请单位证书+法人证书, 5 申请法人证书}
    showLegalSealFile() {
      return [1, 5].includes(this.productValue)
    },
    // 上海CA 法人证书类业务（5 申请法人证书 / 6 续费法人证书）：
    // CA 厂商接口校验法定代表人与经办人必须为同一人，
    // 法定代表人姓名/身份证号锁定为经办人值并置灰（syncLegalFields 负责同步）
    legalFieldsDisabled() {
      return this.caType === CA_TYPE.SHCA && [5, 6].includes(this.productValue)
    },
    // 选中业务类型下的年限档列表
    limitList() {
      return (this.bizType && this.bizType.limitList) || []
    },
    // 左侧产品图：取 system.productFileId 配置；未配置或加载失败用本地 CFCA 图兜底
    productImgSrc() {
      const productFileId = this.pageData?.customer?.productFileId
      return productFileId
        ? `/zcjf/base/file/download/${productFileId}`
        : require('@/views/ca/assets/CFCA.jpg')
    },
    // 加急选项（卡片式单选，与年限档同一交互语言）
    urgentOptions() {
      return [
        { value: 1, label: '需要' },
        { value: 0, label: '不需要' },
      ]
    },
    // 当前年限档是否允许选加急（urgentFlag '1' 支持 / '0' 不支持）
    urgentShow() {
      return this.currentYear && String(this.currentYear.urgentFlag) === '1'
    },
    // 加急费取当前年限档配置（urgentFee，整单一次性费用），与总价计算同源，不写死
    urgentFee() {
      return this.currentYear ? this.currentYear.urgentFee : 0
    },
    // 总价 = 档位单价 × 数量 + (加急 ? 加急费 : 0)，加急费整单只收一次
    total() {
      return calcTotalPrice(this.currentYear, this.quantity, this.isUrgent)
    },
    // 客户免责提示：仅取后台「客户配置」customer.disclaimerNotice，未配置返回空串（不展示）
    platformNotice() {
      const customer = (this.pageData && this.pageData.customer) || {}
      return customer.disclaimerNotice || ''
    },
    // 申请页三段描述文案：仅取后台「客户配置」customer 下的同名字段，未配置返回空串（不展示）
    renewNotice() {
      return this.resolveNotice('renewNotice')
    },
    deliveryNotice() {
      return this.resolveNotice('deliveryNotice')
    },
    refundNotice() {
      return this.resolveNotice('refundNotice')
    },
    // 加急提示：取后台「客户配置」customer.urgentNotice，未配置返回空串（不展示）；
    // 文案中可用 {urgentFee} 占位当前年限档的加急费，展示时替换为实际金额
    urgentNotice() {
      const notice = this.resolveNotice('urgentNotice')
      return notice ? notice.replace(/\{urgentFee\}/g, this.urgentFee) : ''
    },
    // 授权提示：取后台「客户配置」customer.authorizeNotice，未配置返回空串（不展示），
    // CFCA 展示在「被授权人信息」区块内，上海CA 展示在「经办人/申请单位信息」区块内
    authorizeNotice() {
      return this.resolveNotice('authorizeNotice')
    },
    // CA申请授权书字段下方固定展示的模板下载文件：
    // 取页面配置 templateFileList 中 templateCode === 'SQS'（授权书）的那一个
    authTemplateFile() {
      const files = (this.pageData && this.pageData.templateFileList) || []
      return files.find((f) => f.templateCode === TEMPLATE_CODE.AUTH)
    },
    // 单位公章图片字段下方固定展示的模板下载文件：
    // 取页面配置 templateFileList 中 templateCode === 'DWGZ'（单位公章）的那一个
    orgSealTemplateFile() {
      const files = (this.pageData && this.pageData.templateFileList) || []
      return files.find((f) => f.templateCode === TEMPLATE_CODE.ORG_SEAL)
    },
    // 传给确认弹框的快照数据（选型 + 表单关键字段）
    confirmSnapshot() {
      return {
        caType: this.caType,
        payType: this.payType,
        platformName: this.platformName,
        bizTypeName: this.bizType && this.bizType.productName,
        numberVal: this.quantity,
        limitName: this.currentYear && this.currentYear.limitName,
        // 当前年限档是否支持加急：false 时确认弹框不展示「是否加急」行
        urgentShow: this.urgentShow,
        isUrgent: this.isUrgent,
        total: this.total,
        form: this.form,
      }
    },
  },
  watch: {
    customerCode: {
      immediate: true,
      handler(code) {
        if (!code) return
        // 客户切换（路由复用组件不会重建）→ 清掉上一客户的选型，避免旧 productId 脏数据
        this.bizType = null
        this.currentYear = null
        this.loadPageData(code)
      },
    },
    pageData: {
      immediate: true,
      handler(pd) {
        if (!pd) return
        // 使用平台名与页面「1. 选择使用平台」同源（customer.platformName，单平台固定选中）；默认业务类型 / 年限档
        this.platformName = (pd.customer || {}).platformName || ''
        if (!this.bizType && pd.productList && pd.productList.length) {
          this.handleBizTypeChange(pd.productList[0])
        }
      },
    },
    // 法人证书类业务下经办人即法定代表人，经办人姓名/身份证号变化时同步锁定值
    'form.linkMan'() {
      this.syncLegalFields()
    },
    'form.identityCode'() {
      this.syncLegalFields()
    },
    // 「去结算」被未登录拦下后，登录弹框关闭时若已拿到登录凭证（登录成功）→ 自动补开结算确认弹框；
    // 用户放弃登录直接关闭弹框（仍无凭证）→ 清掉待办，不打开确认弹框
    loginVisible(visible) {
      if (visible || !this.pendingSettle) return
      this.pendingSettle = false
      if (isLogin()) this.confirmVisible = true
    },
  },
  created() {
    // 预注册全部字段 key：Vue 2 中给对象新增属性不是响应式的，
    // form 初始为 {} 时 v-model 绑不上；先 $set 一遍空值占位
    CA_FORM_KEYS.forEach((key) => this.$set(this.form, key, ''))
    // 收件地区级联选中值（区划码数组，仅作级联 v-model 与校验用，不进提交 payload）
    this.$set(this.form, 'receiverArea', [])
  },
  methods: {
    download,
    // 描述文案取值：仅取客户配置 customer 下的字段，未配置返回空串
    resolveNotice(key) {
      const customer = (this.pageData && this.pageData.customer) || {}
      return customer[key] || ''
    },
    // 申请页整体配置（apply/applyInfo）：拉取失败或返回空 → 页面级错误占位 + 重试
    loadPageData(code) {
      const c = code || this.customerCode
      if (!c) return
      this.pageDataError = false
      getPageData({ customerCode: c })
        .then((res) => {
          this.pageData = res.data
          if (!this.pageData) this.pageDataError = true
        })
        .catch(() => {
          this.pageDataError = true
        })
    },
    // 配置的产品图下载失败 → 回退本地兜底图；已回退过就不再重复触发
    handleProductImgError(e) {
      const fallback = require('@/views/ca/assets/CFCA.jpg')
      if (e.target.src !== fallback) e.target.src = fallback
    },
    // 表单区块标题前的展示序号：数量板块（第 3 个，上海CA 隐藏）/ 加急区（第 5 个）隐藏时，
    // 其后区块序号各前移一位
    sectionNo(sec) {
      let n = sec
      if (sec > 3 && !this.showQuantity) n -= 1
      if (sec > 5 && !this.urgentShow) n -= 1
      return n
    },
    // 身份证号 / 统一社会信用代码输入时自动转大写（这两个字段的校验正则只认大写字母）
    uppercase(key) {
      this.form[key] = (this.form[key] || '').toUpperCase()
    },
    handleFileChange(key, res) {
      this.$set(this.form, key, res.url || '')
      this.$refs.form && this.$refs.form.validateField(key)
    },
    // 收件地区级联选中后拆分出省/市/区县三个字段（提交中文名，区划码仅留在 receiverArea 作级联 v-model）
    handleReceiverAreaChange() {
      const node = this.$refs.receiverAreaCascader.getCheckedNodes()[0]
      const [province, city, zone] = (node && node.pathLabels) || []
      this.form.receiverProvince = province || ''
      this.form.receiverCity = city || ''
      this.form.receiverZone = zone || ''
    },
    // 切换业务类型：年限重置为该类型第一档、按新档 urgentFlag 重置加急选择；申请数量保持不变
    handleBizTypeChange(b) {
      this.bizType = b
      this.currentYear = b.limitList[0] || null
      this.resetUrgent()
      // 切入法人证书类业务时立即按当前经办人值锁定法定代表人字段
      this.syncLegalFields()
    },
    // 上海CA 法人证书类业务（legalFieldsDisabled）：CA 接口校验法定代表人与经办人必须一致，
    // 法定代表人姓名/身份证号置灰并跟随经办人值（经办人身份证号输入时已转大写，直接拷贝）
    syncLegalFields() {
      if (!this.legalFieldsDisabled) return
      this.form.legalName = this.form.linkMan
      this.form.legalIdNo = this.form.identityCode
    },
    handleYearChange(y) {
      this.currentYear = y
      this.resetUrgent()
    },
    // 新年限档不允许加急（urgentShow≠1）时，强制把加急选择清空回「不需要」
    resetUrgent() {
      if (!this.urgentShow) this.isUrgent = 0
    },
    handleQuantityBlur() {
      this.numberVal = normalizeQuantity(this.numberVal)
    },
    /**
     * 点「去结算」：
     * 1. 表单校验失败 → 滚动定位到第一个错误字段，中断；
     * 2. 未登录 → 弹登录框并记下待办，登录成功后自动补开确认弹框（见 loginVisible 监听）；
     * 3. 已登录 → 打开申请信息确认弹框。
     */
    handleSettle() {
      this.$refs.form.validate((valid) => {
        if (!valid) {
          const el = document.querySelector('.ca-apply__form .el-form-item.is-error')
          if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        if (!isLogin()) {
          this.pendingSettle = true
          showLogin()
          return
        }
        this.confirmVisible = true
      })
    },
    /**
     * 组装下单入参 CaOrderSubmitVo（接口文档 §4.4）。
     *  - numberVal / isUrgent / payType 后端是 String 类型，统一字符串化；
     *  - agreeFlag 协议接口已废弃，页面无勾选项，按后端约束固定传 '1'；
     *  - customerId 记录下单来源，取 applyInfo 返回的 customer.id；
     *  - 表单 key 与后端一致、直接取值；上海CA 条件字段仅当前业务类型下可见时才进 payload。
     */
    buildPayload() {
      const f = this.form
      const base = {
        customerId: ((this.pageData && this.pageData.customer) || {}).id || '',
        productId: this.bizType.id,
        numberVal: String(this.quantity),
        limitId: this.currentYear.id,
        isUrgent: String(this.isUrgent),
        agreeFlag: '1',
        payType: String(this.payType),
      }
      if (this.caType === CA_TYPE.CFCA) {
        return {
          ...base,
          linkMan: f.linkMan,
          linkTel: f.linkTel,
          identityCode: f.identityCode,
          linkEmail: f.linkEmail,
          postAddr: f.postAddr,
          applyOrgName: f.applyOrgName,
          licenceCode: f.licenceCode,
          legalName: f.legalName,
          legalIdNo: f.legalIdNo,
          licenceCodeFile: f.licenceCodeFile,
          authFileId: f.authFileId,
        }
      }
      // 上海CA：表单 key 与后端一致，直接取值。
      // identityFileId（经办人身份证扫描件）为固定字段，恒提交；
      // legalName（法定代表人姓名）已全业务放开、恒显必填，恒提交；
      // 仅 licenceCodeFile / legalIdNo / legalIdFileId 等仍按条件字段白名单进 payload
      const payload = {
        ...base,
        linkMan: f.linkMan,
        identityCode: f.identityCode,
        identityFileId: f.identityFileId,
        linkTel: f.linkTel,
        linkEmail: f.linkEmail,
        legalName: f.legalName,
        applyOrgName: f.applyOrgName,
        licenceCode: f.licenceCode,
        receiverName: f.receiverName,
        receiverMobile: f.receiverMobile,
        receiverProvince: f.receiverProvince,
        receiverCity: f.receiverCity,
        receiverZone: f.receiverZone,
        postAddr: f.postAddr,
      }
      if (this.showLicenceCodeFile) payload.licenceCodeFile = f.licenceCodeFile
      if (this.showLegalFields) {
        payload.legalIdNo = f.legalIdNo
        payload.legalIdFileId = f.legalIdFileId
      }
      if (this.showOrgSealFile) payload.orgSealFileId = f.orgSealFileId
      if (this.showLegalSealFile) payload.legalSealFileId = f.legalSealFileId
      return payload
    },
    /**
     * 确认弹框点「确认」后的下单分流：
     *  - 网上支付：下单成功 → 调 signOrder 拿收银台参数 → 动态 form POST 新窗口跳收银台，
     *    本页直接弹支付结果确认（与订单列表页同款，不走 sessionStorage 标记）；
     *  - 电汇：下单成功 → 带电汇页需要的 orderId 跳电汇页，上传付款凭证。
     */
    handleConfirmSubmit() {
      this.submitting = true
      submitOrder(this.buildPayload())
        .then((res) => {
          const orderId = res.data && res.data.orderId
          this.confirmVisible = false
          if (this.payType === PAY_TYPE.ONLINE) {
            return paySign({ orderId }).then((signRes) => {
              if (payCaOrder(signRes.data)) {
                this.paidOrderId = orderId
                this.payResultVisible = true
              }
            })
          }
          this.$router.push({ name: 'ca-wire-transfer', query: { orderId } })
        })
        .finally(() => {
          this.submitting = false
        })
    },
    // 支付结果确认弹框：与订单列表页同一套交互
    handlePayProblem() {
      this.$router.push({ name: 'ca-contact' })
    },
    handlePayDone() {
      this.$router.push({
        name: 'ca-order-detail',
        query: { orderId: this.paidOrderId },
      })
    },
  },
}
</script>
